import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  FolderPlus, Upload, Folder, FileText, ChevronRight, Home, Loader2, Download, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE_URL, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

export const Route = createFileRoute("/documents")({
  component: () => <Guard module="documents"><DocumentsPage /></Guard>,
});

interface FolderEntry { name: string; path: string }
interface FileEntry { name: string; path: string; size: number; modified: string; url: string }
interface ListResponse { bucket: string; path: string; folders: FolderEntry[]; files: FileEntry[] }

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

async function uploadDocument(bucket: "receipts" | "bibliotheque", file: File, path: string): Promise<void> {
  const fd = new FormData();
  fd.append("file", file);
  if (path) fd.append("path", path);
  const token = tokenStore.get();
  const res = await fetch(`${API_BASE_URL}/api/documents/${bucket}/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || `HTTP ${res.status}`);
  }
}

function DocumentsPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Documents"
        description="Bibliothèque partagée et justificatifs des notes de frais."
      />
      <Tabs defaultValue="bibliotheque">
        <TabsList>
          <TabsTrigger value="bibliotheque">
            <Folder className="h-4 w-4 mr-2" /> Bibliothèque
          </TabsTrigger>
          <TabsTrigger value="receipts">
            <Receipt className="h-4 w-4 mr-2" /> Justificatifs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bibliotheque" className="mt-4">
          <Browser bucket="bibliotheque" allowFolders allowUpload />
        </TabsContent>

        <TabsContent value="receipts" className="mt-4">
          <Browser bucket="receipts" allowFolders={false} allowUpload />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Browser({
  bucket,
  allowFolders,
  allowUpload,
}: {
  bucket: "bibliotheque" | "receipts";
  allowFolders: boolean;
  allowUpload: boolean;
}) {
  const { hasAccess } = useAuth();
  const canWrite = hasAccess("documents", "write");
  const qc = useQueryClient();
  const [path, setPath] = useState("");
  const [mkdirOpen, setMkdirOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryKey = ["documents", bucket, path];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const url = path ? `/api/documents/${bucket}?path=${encodeURIComponent(path)}` : `/api/documents/${bucket}`;
      return api.get<ListResponse>(url, { rawJson: true });
    },
  });

  const mkdir = useMutation({
    mutationFn: async (name: string) => {
      await api.post(`/api/documents/bibliotheque/folders`, { path, name }, { rawJson: true });
    },
    onSuccess: async () => {
      toast.success("Dossier créé");
      setMkdirOpen(false);
      setFolderName("");
      await qc.refetchQueries({ queryKey, exact: true });
    },
    onError: (e: Error) => toast.error(e.message || "Erreur lors de la création"),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      await uploadDocument(bucket, file, path);
    },
    onSuccess: async () => {
      toast.success("Fichier ajouté");
      await qc.refetchQueries({ queryKey, exact: true });
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (e: Error) => toast.error(e.message || "Erreur lors de l'upload"),
  });

  const breadcrumbs = useMemo(() => {
    if (!path) return [];
    const parts = path.split("/");
    return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join("/") }));
  }, [path]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    Array.from(files).forEach((f) => upload.mutate(f));
  };

  const handleDownload = async (file: FileEntry) => {
    try {
      const token = tokenStore.get();
      const res = await fetch(file.url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      toast.error("Téléchargement impossible : " + (e as Error).message);
    }
  };

  return (
    <Card className="p-4 space-y-4">
      {/* Breadcrumb + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1 text-sm flex-wrap">
          <button
            onClick={() => setPath("")}
            className="flex items-center gap-1 hover:text-primary transition-colors"
          >
            <Home className="h-4 w-4" />
            <span>Racine</span>
          </button>
          {breadcrumbs.map((bc) => (
            <div key={bc.path} className="flex items-center gap-1">
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <button
                onClick={() => setPath(bc.path)}
                className="hover:text-primary transition-colors"
              >
                {bc.name}
              </button>
            </div>
          ))}
        </div>

        {canWrite && allowUpload && (
          <div className="flex items-center gap-2">
            {allowFolders && (
              <Button size="sm" variant="outline" onClick={() => setMkdirOpen(true)}>
                <FolderPlus className="h-4 w-4 mr-2" /> Nouveau dossier
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Ajouter un fichier
            </Button>
          </div>
        )}
      </div>

      {/* Contenu */}
      {isLoading && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
          Chargement…
        </div>
      )}

      {error && (
        <div className="py-12 text-center text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

  {!isLoading && !error && data && (() => {
        const visibleFiles = data.files.filter((f) => f.name !== ".gitkeep");
        return (
        <div className="border rounded-md divide-y">
          {data.folders.length === 0 && visibleFiles.length === 0 && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Ce dossier est vide.
            </div>
          )}

          {data.folders.map((f) => (
            <button
              key={f.path}
              onClick={() => setPath(f.path)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
            >
              <Folder className="h-5 w-5 text-primary shrink-0" />
              <span className="font-medium flex-1">{f.name}</span>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}

          {visibleFiles.map((f) => (
            <div
              key={f.path}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
            >
              <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{f.name}</div>
                <div className="text-xs text-muted-foreground">
                  {formatSize(f.size)} · {new Date(f.modified).toLocaleDateString("fr-FR")}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDownload(f)}>
                <Download className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
        );
      })()}

      {/* Dialog création de dossier */}
      <Dialog open={mkdirOpen} onOpenChange={setMkdirOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau dossier</DialogTitle>
            <DialogDescription>
              {path ? `Créer un sous-dossier dans « ${path} ».` : "Créer un dossier à la racine."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Nom du dossier</Label>
            <Input
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Ex : Compte-rendus AG"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              Lettres, chiffres, espaces, tirets et points uniquement.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMkdirOpen(false)}>Annuler</Button>
            <Button
              onClick={() => folderName.trim() && mkdir.mutate(folderName.trim())}
              disabled={!folderName.trim() || mkdir.isPending}
            >
              {mkdir.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
